import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PageMeta from '../SEO/PageMeta';
import { LayoutGrid, X, ChevronLeft, ChevronRight } from 'lucide-react';
import PublicFooter from '../PublicFooter/PublicFooter';
import NewsArticleReader from './NewsArticleReader';
import logoImg from '../../assets/logo.png';
import templeImg from '../../assets/temple-far1.jpg';
import './NewsPage.css';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://civilfox-backend.onrender.com/api',
});
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('vidyacards_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const PAGE_SIZE = 40;
const PREFETCH_PAGES = 3;   // enough stories up front to fill the subject list
const MAX_PAGES = 12;       // never auto-load more than this

const decode = value => {
  const entities = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&nbsp;': ' ', '&mdash;': '—', '&ndash;': '–', '&hellip;': '…', '&rsquo;': '\u2019', '&lsquo;': '\u2018', '&rdquo;': '\u201D', '&ldquo;': '\u201C', '&apos;': "'" };
  return String(value || '').replace(/&[a-z#0-9]+;/gi, e => entities[e] || e);
};
const subjectOf = a => (typeof a?.subject === 'string' && a.subject.trim()) || 'Current Affairs';
const snippetOf = a => decode(a?.description || a?.upscExplanationStructured?.what || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
const shortDate = v => (v ? new Date(v).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' }) : '');

/* Keeps one bad article from blanking the whole page */
class Boundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error) { console.error('NewsPage render error:', error); }
  componentDidUpdate(prev) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }
  render() {
    if (!this.state.error) return this.props.children;
    return <p className="nw-state">Could not display this story. {String(this.state.error.message || '')}</p>;
  }
}

export default function NewsPage() {
  const isLoggedIn = !!localStorage.getItem('vidyacards_token');

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [subject, setSubject] = useState('All');
  const [selectedId, setSelectedId] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const bodyRef = useRef(null);
  const mainRef = useRef(null);
  const touch = useRef(null);

  /* Load stories, page by page */
  const load = useCallback(async (pageNum, append) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await api.get('/news/live', { params: { limit: PAGE_SIZE, page: pageNum } });
      const incoming = (res.data?.articles || []).filter(a => a && a._id);
      setArticles(prev => {
        const byId = new Map(append ? prev.map(a => [a._id, a]) : []);
        incoming.forEach(a => byId.set(a._id, a));
        return [...byId.values()];
      });
      setTotal(res.data?.total || 0);
      setHasMore(pageNum < (res.data?.pages || 1));
      setPage(pageNum);
    } catch (err) {
      console.error(err);
      if (append) setHasMore(false); else setFailed(true);
    } finally { setLoading(false); setLoadingMore(false); }
  }, []);

  useEffect(() => { load(1, false); }, [load]);

  /* Full-screen page: lock the page behind it */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.overflow, body.style.overflow];
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => { html.style.overflow = prev[0]; body.style.overflow = prev[1]; };
  }, []);

  /* Subjects, most stories first */
  const subjects = useMemo(() => {
    const counts = new Map();
    articles.forEach(a => counts.set(subjectOf(a), (counts.get(subjectOf(a)) || 0) + 1));
    return [...counts.entries()].sort((x, y) => y[1] - x[1]).map(([s]) => s);
  }, [articles]);

  const list = useMemo(
    () => (subject === 'All' ? articles : articles.filter(a => subjectOf(a) === subject)),
    [articles, subject]
  );

  useEffect(() => {
    if (!list.length) { setSelectedId(null); return; }
    if (!list.some(a => a._id === selectedId)) setSelectedId(list[0]._id);
  }, [list, selectedId]);

  const idx = list.findIndex(a => a._id === selectedId);
  const current = idx > -1 ? list[idx] : null;

  /* Keep loading while the reader nears the end of the list, and up front for the subject list */
  useEffect(() => {
    if (!hasMore || loading || loadingMore || page >= MAX_PAGES) return;
    if (page < PREFETCH_PAGES || idx >= list.length - 5) load(page + 1, true);
  }, [hasMore, loading, loadingMore, page, idx, list.length, load]);

  /* Similar news: the rest of this subject; under "All", the current subject comes first */
  const related = useMemo(() => {
    if (!current) return [];
    const rest = list.filter(a => a._id !== current._id);
    if (subject !== 'All') return rest.slice(0, 40);
    const same = rest.filter(a => subjectOf(a) === subjectOf(current));
    return [...same, ...rest.filter(a => subjectOf(a) !== subjectOf(current))].slice(0, 40);
  }, [list, current, subject]);

  useEffect(() => {
    [bodyRef, mainRef].forEach(r => r.current?.scrollTo?.(0, 0));
  }, [selectedId]);

  /* Previous / next within the chosen subject */
  const goNext = useCallback(() => { if (idx > -1 && idx < list.length - 1) setSelectedId(list[idx + 1]._id); }, [idx, list]);
  const goPrev = useCallback(() => { if (idx > 0) setSelectedId(list[idx - 1]._id); }, [idx, list]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') { setNavOpen(false); return; }
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  /* Swipe left / right to move between stories */
  const onTouchStart = e => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = e => {
    const t = touch.current;
    touch.current = null;
    if (!t) return;
    const dx = e.changedTouches[0].clientX - t.x;
    const dy = e.changedTouches[0].clientY - t.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) (dx < 0 ? goNext : goPrev)();
  };

  const open = id => { setSelectedId(id); setNavOpen(false); };
  const pick = s => { setSubject(s); setNavOpen(false); };

  const count = subject === 'All' && total ? total : list.length;
  const progress = count && idx > -1 ? ((idx + 1) / count) * 100 : 0;

  return (
    <div className="nw">
      <PageMeta
        title="UPSC Current Affairs & News Today | CivilFox"
        description="Read UPSC current affairs and India news with concise Prelims and Mains analysis, practice questions, and exam-focused explanations on CivilFox."
        path="/news"
      />
      {/* Panel: fixed, only the subject list inside it scrolls */}
      <aside className={`nw-side ${navOpen ? 'open' : ''}`} aria-label="Subjects">
        <img className="nw-side-art" src={templeImg} alt="" />
        <div className="nw-side-body">
          <div className="nw-side-top">
            <Link to={isLoggedIn ? '/dashboard' : '/'} className="nw-logo" aria-label="CivilFox home"><img src={logoImg} alt="" /></Link>
            <button type="button" className="nw-x" onClick={() => setNavOpen(false)} aria-label="Close"><X size={18} /></button>
          </div>
          <nav className="nw-cats">
            {['All', ...subjects].map(s => (
              <button key={s} type="button" className={`nw-cat ${s === subject ? 'on' : ''}`}
                aria-current={s === subject ? 'true' : undefined} onClick={() => pick(s)}>
                <i className="nw-dot" />{s}
              </button>
            ))}
          </nav>
        </div>
      </aside>

      <div className="nw-body" ref={bodyRef}>
        {/* Centre: the story */}
        <main className="nw-main" ref={mainRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className="nw-progress" aria-hidden="true"><i style={{ width: `${progress}%` }} /></div>

          <div className="nw-head">
            <button type="button" className="nw-grid-btn" onClick={() => setNavOpen(true)} aria-label="All subjects">
              <LayoutGrid size={18} />
            </button>
            <h2 className="nw-title">{subject === 'All' ? 'News' : subject}</h2>
          </div>

          {loading ? (
            <div className="nw-state"><span className="nw-spin" role="status" aria-label="Loading" /><p>Loading UPSC news…</p></div>
          ) : !articles.length ? (
            <div className="nw-state">
              {failed ? <p>Could not load news.</p> : (
                <>
                  <h3>I am reading newspaper for you right now</h3>
                  <p>Till then you practise prelims</p>
                </>
              )}
            </div>
          ) : !current ? (
            <p className="nw-state">No stories.</p>
          ) : (
            <>
              <Boundary resetKey={current._id}>
                <NewsArticleReader
                  key={current._id}
                  article={current}
                  onShare={a => api.post(`/news/share/${a._id}`)}
                />
              </Boundary>

              <div className="nw-nav">
                <button type="button" className="nw-nbtn" onClick={goPrev} disabled={idx <= 0} aria-label="Previous story"><ChevronLeft size={18} /></button>
                <span className="nw-counter"><b>{idx + 1}</b> / {count}</span>
                <button type="button" className="nw-nbtn" onClick={goNext} disabled={idx >= list.length - 1 && !hasMore} aria-label="Next story"><ChevronRight size={18} /></button>
              </div>
              {loadingMore && <p className="nw-more">Loading more…</p>}
            </>
          )}
          <PublicFooter />
        </main>

        {/* Right: similar news */}
        <aside className="nw-related" aria-label="Related news">
          <div className="nw-head"><h2 className="nw-title">Related news</h2></div>
          {related.map(a => (
            <button key={a._id} type="button" className="nw-rel" onClick={() => open(a._id)}>
              {a.imageUrl && (
                <img src={a.imageUrl} alt="" loading="lazy" onError={e => { e.currentTarget.style.display = 'none'; }} />
              )}
              <span className="nw-rel-top">
                <span className="nw-chip">{subjectOf(a)}</span>
                {a.publishedAt && <time dateTime={a.publishedAt}>{shortDate(a.publishedAt)}</time>}
              </span>
              <h3>{decode(a.title)}</h3>
              {snippetOf(a) && <p>{snippetOf(a)}</p>}
            </button>
          ))}
        </aside>
      </div>

      <div className={`nw-backdrop ${navOpen ? 'show' : ''}`} onClick={() => setNavOpen(false)} />
    </div>
  );
}